const ACTIVE_STATUS = new Set([
  "active",
  "trialing",
  "trial",
  "premium",
  "pro",
  "paid",
  "grace_period",
  "in_grace_period",
]);

const INACTIVE_STATUS = new Set([
  "inactive",
  "expired",
  "cancelled",
  "canceled",
  "free",
  "none",
]);

function readDateValue(value) {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  if (value instanceof Date) {
    const ts = value.getTime();
    return Number.isNaN(ts) ? null : ts;
  }

  if (typeof value === "number") {
    if (!Number.isFinite(value)) return null;
    return value > 1e12 ? value : value * 1000;
  }

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return null;

    if (/^\d+$/.test(trimmed)) {
      const numeric = Number(trimmed);
      return numeric > 1e12 ? numeric : numeric * 1000;
    }

    const parsed = Date.parse(trimmed);
    return Number.isNaN(parsed) ? null : parsed;
  }

  return null;
}

function toBoolean(value) {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value > 0;
  if (typeof value !== "string") return null;

  const normalized = value.trim().toLowerCase();
  if (!normalized) return null;

  if (
    ["true", "1", "yes", "y", "pro", "premium", "active"].includes(normalized)
  ) {
    return true;
  }

  if (
    ["false", "0", "no", "n", "free", "inactive", "expired"].includes(
      normalized
    )
  ) {
    return false;
  }

  return null;
}

function firstDefined(...values) {
  return values.find(
    (value) => value !== undefined && value !== null && value !== ""
  );
}

function normalizeStatus(status) {
  if (!status && status !== 0) return "";
  return String(status).trim().toLowerCase();
}

function hasTrustedSubscriptionSource(source) {
  const normalized = normalizeStatus(source);
  if (!normalized) return false;

  return !["none", "user", "account", "profile", "unknown"].includes(
    normalized
  );
}

export function getSubscriptionSnapshot(user, now = Date.now()) {
  if (!user || typeof user !== "object") {
    return {
      isPro: false,
      status: "free",
      provider: null,
      plan: null,
      expiresAt: null,
      source: "none",
    };
  }

  const subscription =
    user.subscription && typeof user.subscription === "object"
      ? user.subscription
      : {};
  const entitlements =
    user.entitlements && typeof user.entitlements === "object"
      ? user.entitlements
      : {};

  const expiresAt = readDateValue(
    firstDefined(
      subscription.expiresAt,
      subscription.expires_at,
      subscription.expiryDate,
      subscription.expiry_date,
      subscription.validTo,
      subscription.valid_to,
      user.subscriptionExpiresAt,
      user.subscription_expires_at,
      user.subscription_valid_to,
      user.proUntil,
      entitlements.pro?.expiresAt,
      entitlements.pro?.expires_at
    )
  );

  const rawStatus = firstDefined(
    subscription.status,
    user.subscription_status,
    user.planStatus,
    user.plan_status,
    user.membershipStatus,
    user.membership_status
  );

  const normalizedStatus = normalizeStatus(rawStatus);
  const rawProvider = firstDefined(
    subscription.provider,
    subscription.source,
    subscription.store,
    user.subscription_provider,
    user.subscription_source,
    entitlements.pro?.provider
  );
  const normalizedProvider = normalizeStatus(rawProvider);
  const directBoolean = firstDefined(
    toBoolean(user.isPro),
    toBoolean(user.pro),
    toBoolean(entitlements.pro?.active),
    toBoolean(entitlements.pro),
    toBoolean(user.premium)
  );
  const subscriptionBoolean = firstDefined(
    toBoolean(subscription.active),
    toBoolean(subscription.isActive)
  );

  const statusSaysActive = ACTIVE_STATUS.has(normalizedStatus);
  const statusSaysInactive = INACTIVE_STATUS.has(normalizedStatus);
  const expirySaysActive = Boolean(expiresAt && expiresAt > now);
  const hasTrustedSource = hasTrustedSubscriptionSource(normalizedProvider);

  const isPro =
    directBoolean === true ||
    (hasTrustedSource && subscriptionBoolean === true) ||
    (hasTrustedSource && statusSaysActive) ||
    (hasTrustedSource && !statusSaysInactive && expirySaysActive);

  return {
    isPro,
    status: normalizedStatus || (isPro ? "active" : "free"),
    provider: normalizedProvider || null,
    plan:
      firstDefined(
        subscription.plan,
        subscription.productId,
        subscription.product_id,
        user.plan,
        user.membership,
        entitlements.pro?.productId
      ) || null,
    expiresAt,
    source:
      firstDefined(
        subscription.source,
        subscription.provider,
        user.subscription_source,
        user.subscription_provider
      ) || "none",
  };
}

export function formatSubscriptionDate(timestamp) {
  if (!timestamp) return null;

  try {
    return new Intl.DateTimeFormat("en-GB", {
      year: "numeric",
      month: "short",
      day: "numeric",
    }).format(new Date(timestamp));
  } catch {
    return null;
  }
}
